import { createEffect, sample } from 'effector';
import { get, pick } from 'lodash';
import { $answers } from 'models/answers';
import $$api from 'models/api';
import $$auth from 'models/auth';
import $$giftTrust from 'models/gift-trusts';
import { $qInstances } from 'models/q-instances';
import navigate from 'models/router-gate';
import { $global } from 'models/store-global';

import './jobs.init.ts';

import GiftTrustService from 'services/GiftTrustService';
import { createTunnel } from 'utils/factories/create-tunnel';

import { type Document } from 'types/zod/document';
import { type QuestionnaireInstance } from 'types/zod/questionnaire-instance';

$$giftTrust.createNewInstanceFx.use(async () => {
    return await $$api.qInstances.createInstanceFx('GIFT_TRUST');
});

$$giftTrust.getAnswersForAllInstancesFx.use(async (ids) => {
    await Promise.all(ids.map((id) => $$api.answers.getAnswersFx({ id })));
});

$$giftTrust.getAnswersForOneInstanceFx.use(async (id) => {
    await $$api.answers.getAnswersFx({ id });
});

$$giftTrust.isPaymentOkBulkFx.use(async (ids) => {
    return await Promise.all(ids.map((id) => $$api.qInstances.isPaymentOkFx(id)));
});

$$giftTrust.updateAnswersFx.use(async ({ qInstanceId, answers }) => {
    await $$api.answers.putAnswersFx({ id: qInstanceId, answers });
});

$$giftTrust.setIsSignedFx.use(async ({ qInstanceId, answers }) => {
    return await $$api.answers.putAnswersFx({ id: qInstanceId, answers: get(answers, 'hidden'), path: 'hidden' });
});

$$giftTrust.generateGiftItemFx.use(async ({ qInstanceId, giftTrustItemId }) => {
    return await $$api.documents.generateDocByCodeFx({
        questionnaireInstanceId: qInstanceId,
        tz: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
        documentType: 'Gift_Trust_Letter',
        giftRowId: giftTrustItemId,
    });
});

sample({
    source: $qInstances.map((qInstances) =>
        Object.values(qInstances).filter((qInstance) => qInstance.questionnaire_type_code === 'GIFT_TRUST'),
    ),
    target: $$giftTrust.$qInstances,
});

sample({
    clock: $$giftTrust.createNewInstanceFx.doneData,
    fn: (result) =>
        `/document/${result.questionnaire_type_code}/${result.state_code}/${result.questionnaire_instance_id}`,
    target: createEffect((path: string) => navigate(path)),
});

sample({
    source: {
        questionnaires: $global.map((state) => state.questionnaires),
        state: $$auth.$currentParticipant.map((p) => p?.address?.domicile?.state_code),
    },
    filter: ({ questionnaires, state }) => !!questionnaires && !!state,
    fn: ({ questionnaires, state }) => (state && questionnaires?.[state]?.GIFT_TRUST) || null,
    // target: $$giftTrust.$questionnaire,
});

sample({
    source: $$giftTrust.$qInstances.map((qInstances) =>
        qInstances.map((qInstance) => qInstance?.questionnaire_instance_id),
    ),
    target: $$giftTrust.getAnswersForAllInstancesFx,
});

sample({
    source: createTunnel(
        sample({
            source: {
                qInstancesIds: $$giftTrust.$qInstances.map((qInstances) =>
                    qInstances.map((qInstance) => qInstance?.questionnaire_instance_id),
                ),
                answers: $answers,
            },
            fn: ({ qInstancesIds, answers }) => pick(answers, qInstancesIds),
        }),
        (d) => d,
    ),
    target: $$giftTrust.$answers,
});

sample({
    source: $$giftTrust.$answers,
    fn: GiftTrustService.getGiftTrustItemsForAllQInstances,
    target: $$giftTrust.$giftTrustItems,
});

sample({
    source: $$giftTrust.$answers,
    clock: $$giftTrust.removeGiftItem,
    fn: (allAnswers, { qInstanceId, giftTrustItemId }) => {
        const answers = allAnswers[qInstanceId];
        if (!answers) {
            throw new Error(`Answers for qInstanceId ${qInstanceId} not found`);
        }
        const updatedAnswers = GiftTrustService.removeGiftTrustItem({ answers, giftTrustItemId });
        return { id: qInstanceId, answers: updatedAnswers };
    },
    target: $$api.answers.putAnswersFx,
});

sample({
    source: {
        communityDocs: $global.map((state) => state.community?.docs || []),
        qInstancesIds: $$giftTrust.$qInstances.map((qInstances) =>
            qInstances.map((qInstance) => qInstance?.questionnaire_instance_id),
        ),
    },
    fn: ({ communityDocs, qInstancesIds }) => {
        return qInstancesIds.reduce(
            (acc, id) => ({ ...acc, [id]: communityDocs.filter((doc) => doc.questionnaire_instance_id === id) }),
            {} as Record<QuestionnaireInstance['questionnaire_instance_id'], Document[]>,
        );
    },
    target: $$giftTrust.$documents,
});

sample({
    source: $$giftTrust.$qInstances.map((qInstances) =>
        qInstances.map((qInstance) => qInstance?.questionnaire_instance_id),
    ),
    target: $$giftTrust.isPaymentOkBulkFx,
});

sample({
    source: {
        isPayed: $$giftTrust.$isPayed,
        qInstancesIds: $$giftTrust.$qInstances.map((qInstances) =>
            qInstances.map((qInstance) => qInstance?.questionnaire_instance_id),
        ),
    },
    clock: $$api.qInstances.isPaymentOkFx.done.map(({ params, result: { result: isPaymentOk } }) => ({
        qInstanceId: params,
        isPaymentOk,
    })),
    filter: ({ qInstancesIds }, { qInstanceId }) => !!qInstancesIds.includes(qInstanceId),
    fn: ({ qInstancesIds, isPayed }, { qInstanceId, isPaymentOk }) => {
        return qInstancesIds.reduce(
            (acc, id) => ({ ...acc, [id]: id === qInstanceId ? isPaymentOk : isPayed[id] || false }),
            {} as Record<QuestionnaireInstance['questionnaire_instance_id'], boolean>,
        );
    },
    target: $$giftTrust.$isPayed,
});
